import { Controller } from '@hotwired/stimulus';
import { Turbo } from '@hotwired/turbo-rails';

export default class extends Controller {
  static targets = [
    'file',
    'form',
    'button',
    'container',
    'programSelect',
    'selectedRbfClaimTemplate',
  ];
  static values = {
    rbfClaimTemplateId: Number,
    mode: String,
    formOptionsPath: String,
    automaticSampling: Boolean,
    generateTabId: String,
    manualTabTitle: String,
    automaticTabTitle: String,
  };

  connect() {
    this.formOptionsRequestId = 0;
    this.formOptionsAbortController = null;

    // Upload forms should only react to file selection; they don't have claim checkboxes.
    // The generic change listener below would immediately disable the button again.
    if (this.hasFileTarget) {
      this.toggleButtonState(Boolean(this.fileTarget.files?.length || this.fileTarget.value));
      this.bindSubmitLoadingState();
      return;
    }

    this.updateGenerateButtonState();
    this.element.addEventListener('change', () => this.updateGenerateButtonState());
    // fetchFormOptions runs on the nested inner controller; it dispatches this event
    // so the outer controller (which owns the button) can react when the panel loads.
    this.element.addEventListener('rbf:form-options-loaded', () =>
      this.updateGenerateButtonState()
    );

    this.configureSubmitAction();
    this.syncSelectedRbfClaimTemplate();
    this.toggleSectionTitle();
    this.autoSubmitProgramSelectionOnLoad();

    this.bindSubmitLoadingState();
  }

  programChanged(event) {
    const mode = this.hasModeValue ? this.modeValue : 'sampling';
    const formOptionsFrame = document.getElementById(`${mode}_dynamic_form_fields_list`);
    if (formOptionsFrame) formOptionsFrame.innerHTML = '';

    this.syncSelectedRbfClaimTemplate();
    this.configureSubmitAction();
    this.toggleSectionTitle();
    event.target.form.requestSubmit();
  }

  async fetchFormOptions(event) {
    if (!this.hasRbfClaimTemplateIdValue) return;
    // followup has no field picker, so there is nothing to fetch.
    if ((this.hasModeValue ? this.modeValue : 'sampling') === 'followup') return;

    this.formOptionsRequestId += 1;
    const requestId = this.formOptionsRequestId;
    if (this.formOptionsAbortController) this.formOptionsAbortController.abort();
    this.formOptionsAbortController = new AbortController();

    const id = this.rbfClaimTemplateIdValue;
    const claimIds = this.selectedClaimIds();
    const selectedSamplingType = this.selectedSamplingType();
    const mode = this.hasModeValue ? this.modeValue : 'sampling';
    const params = new URLSearchParams({ rbf_claim_template_id: id, mode: mode });
    claimIds.forEach((claimId) => params.append('claim_ids[]', claimId));
    if (selectedSamplingType)
      params.append('automatic_sampling[sampling_type]', selectedSamplingType);

    // formOptionsPath lets other pages reuse this panel against their own endpoint
    // (followup claim creation); unset it stays on the external-claim-verification route.
    const path = this.shouldFetchAutomaticSamplingOptions()
      ? '/external_claim_verification/automatic_sampling_options'
      : this.hasFormOptionsPathValue
        ? this.formOptionsPathValue
        : '/external_claim_verification/dynamic_form_fields';

    try {
      const response = await fetch(`${path}?${params.toString()}`, {
        headers: { Accept: 'text/vnd.turbo-stream.html' },
        signal: this.formOptionsAbortController.signal,
      });
      if (!response.ok) return;

      const html = await response.text();
      // Ignore stale responses that arrive after a newer request.
      if (requestId !== this.formOptionsRequestId) return;
      await Turbo.renderStreamMessage(html);
    } catch (error) {
      if (error?.name !== 'AbortError') throw error;
      return;
    }

    // Notify the outer controller (which owns the button) that the options panel is ready.
    // Use requestAnimationFrame to ensure the DOM is fully updated before checking.
    requestAnimationFrame(() => {
      this.element.dispatchEvent(new CustomEvent('rbf:form-options-loaded', { bubbles: true }));
    });
  }

  toggleIncludeSampled(event) {
    if (!this.hasRbfClaimTemplateIdValue) return;
    const includeSampled = event.target.checked;
    const id = this.rbfClaimTemplateIdValue;
    const mode = this.hasModeValue ? this.modeValue : 'sampling';
    const isVerification = mode === 'verification';
    const turboFrameId = isVerification ? 'verification_claim_list' : 'sampling_claim_list';
    const includeParam = isVerification ? 'include_verified' : 'include_sampled';

    fetch(
      `/external_claim_verification/claims?rbf_claim_template_id=${id}&${includeParam}=${includeSampled}&mode=${mode}`,
      {
        headers: {
          Accept: 'text/vnd.turbo-stream.html',
          'Turbo-Frame': turboFrameId,
        },
      }
    )
      .then((response) => response.text())
      .then((html) => Turbo.renderStreamMessage(html));
  }

  fileSelected() {
    if (!this.hasFileTarget) return;
    const hasFile = this.fileTarget.files.length > 0;
    this.toggleButtonState(hasFile);
  }

  bindSubmitLoadingState() {
    if (!this.hasFormTarget || !this.hasButtonTarget) return;
    this.formTarget.addEventListener('submit', () => {
      const loadingText =
        (this.automaticSamplingEnabled()
          ? this.formTarget.dataset.automaticLoadingText
          : this.formTarget.dataset.manualLoadingText) || this.formTarget.dataset.loadingText;
      if (loadingText) this.setButtonText(loadingText);
      this.buttonTarget.disabled = true;
    });
  }

  toggleButtonState(enabled) {
    if (!this.hasButtonTarget) return;

    const btn = this.buttonTarget;
    btn.toggleAttribute('disabled', !enabled);

    const toggles = [
      ['bg-grey-1', !enabled],
      ['text-grey-4', !enabled],
      ['cursor-not-allowed', !enabled],
      ['bg-tealLight', enabled],
      ['text-white', enabled],
      ['cursor-pointer', enabled],
    ];

    toggles.forEach(([cls, on]) => btn.classList.toggle(cls, on));
  }

  selectedClaimIds() {
    return Array.from(this.element.querySelectorAll("input[name='claim_ids[]']:checked")).map(
      (input) => input.value
    );
  }

  selectedSamplingType() {
    const checked = this.element.querySelector(
      "input[name='automatic_sampling[sampling_type]']:checked"
    );
    return checked?.value || null;
  }

  configureSubmitAction() {
    if (!this.hasFormTarget || !this.hasButtonTarget) return;
    if (!this.formTarget.dataset.downloadUrl && !this.formTarget.dataset.automaticSamplingUrl)
      return;

    const automaticSampling = this.automaticSamplingEnabled();
    this.formTarget.action = automaticSampling
      ? this.formTarget.dataset.automaticSamplingUrl
      : this.formTarget.dataset.downloadUrl;
    const submitText = automaticSampling
      ? this.formTarget.dataset.automaticSubmitText
      : this.formTarget.dataset.manualSubmitText;
    if (submitText) this.setButtonText(submitText);
    this.setButtonIcon(automaticSampling ? 'funnel' : 'arrow-down-tray');
  }

  automaticSamplingEnabled() {
    if (!this.hasProgramSelectTarget) return false;

    return this.programSelectTarget.selectedOptions[0]?.dataset.automaticSampling === 'true';
  }

  shouldFetchAutomaticSamplingOptions() {
    return (
      (this.hasModeValue ? this.modeValue : 'sampling') === 'sampling' &&
      this.hasAutomaticSamplingValue &&
      this.automaticSamplingValue
    );
  }

  updateGenerateButtonState() {
    const claimSelected = this.selectedClaimIds().length > 0;

    if (!claimSelected) {
      this.toggleButtonState(false);
      return;
    }

    if (this.automaticSamplingEnabled()) {
      // Enable only when a non-disabled sampling type can be selected.
      const selectedRadio = this.element.querySelector(
        "input[name='automatic_sampling[sampling_type]']:checked:not(:disabled)"
      );
      const firstEnabledRadio = this.element.querySelector(
        "input[name='automatic_sampling[sampling_type]']:not(:disabled)"
      );
      this.toggleButtonState(!!(selectedRadio || firstEnabledRadio));
    } else if ((this.hasModeValue ? this.modeValue : 'sampling') === 'followup') {
      // followup has no field picker — the export always carries every field the form offers —
      // so a selected claim is the only thing it waits for.
      this.toggleButtonState(true);
    } else {
      // PII fields are pre-checked by default — enable as soon as the fields panel appears.
      // Matches any picker param name (fields[, verification_fields[ ...): the views derive it
      // from the mode, so listing them here goes stale on the next mode.
      const panelVisible = !!this.element.querySelector("input[name*='fields[']");
      this.toggleButtonState(panelVisible);
    }
  }

  toggleSectionTitle() {
    if (
      !this.hasGenerateTabIdValue ||
      !this.hasManualTabTitleValue ||
      !this.hasAutomaticTabTitleValue
    )
      return;

    const automatic = this.automaticSamplingEnabled();

    const titleEl = document.getElementById(this.generateTabIdValue);
    if (titleEl) {
      titleEl.textContent = automatic ? this.automaticTabTitleValue : this.manualTabTitleValue;
    }

    // Hide the upload panel (and its separator) when automatic sampling handles it server-side
    const uploadSection = document.getElementById('sampling-upload-section');
    const uploadHr = document.getElementById('sampling-upload-hr');
    if (uploadSection) uploadSection.classList.toggle('hidden', automatic);
    if (uploadHr) uploadHr.classList.toggle('hidden', automatic);
  }

  syncSelectedRbfClaimTemplate() {
    if (!this.hasProgramSelectTarget || !this.hasSelectedRbfClaimTemplateTarget) return;

    this.selectedRbfClaimTemplateTarget.value = this.programSelectTarget.value;
  }

  autoSubmitProgramSelectionOnLoad() {
    if (!this.hasProgramSelectTarget) return;

    const shouldAutoSubmit = this.programSelectTarget.dataset.autoSubmitOnLoad === 'true';
    if (!shouldAutoSubmit || !this.programSelectTarget.value) return;

    this.programSelectTarget.form?.requestSubmit();
    this.programSelectTarget.dataset.autoSubmitOnLoad = 'false';
  }

  // ButtonComponent renders <button><span class="flex..."><i>icon</i><span>text</span></span></button>.
  // Setting textContent on the button itself wipes the icon and flex wrapper.
  // This helper updates only the inner text span so the button keeps its shape.
  setButtonText(text) {
    if (!this.hasButtonTarget) return;

    const textSpan = this.buttonTarget.querySelector('span span');
    if (textSpan) {
      textSpan.textContent = text;
    } else {
      this.buttonTarget.textContent = text;
    }
  }

  // Swaps the heroicon SVG inside the button's <i> tag using pre-rendered icons
  // stored in the hidden #sampling_button_icons container.
  setButtonIcon(iconName) {
    if (!this.hasButtonTarget) return;

    const iconContainer = document.getElementById('sampling_button_icons');
    if (!iconContainer) return;

    const source = iconContainer.querySelector(`[data-icon="${iconName}"]`);
    if (!source) return;

    const iTag = this.buttonTarget.querySelector('i');
    if (iTag) iTag.innerHTML = source.innerHTML;
  }
}
